import { observer } from "mobx-react-lite";
import { TextField, Input, DateField } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import type { CalendarDate } from "@internationalized/date";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import type { LegalFormData } from "@/stores/ContractorDetailStore/legal-store/LegalDataSubStore";
import {
  numericOnly,
  applyPhoneMask,
  phoneMaskToRaw,
} from "@/pages/ContractorDetailPage/constants";
import { FormField } from "../form/FormField";

const StepSelfEmployed: React.FC = observer(() => {
  const { legal } = contractorDetailStore;
  const { form, validationErrors: errors } = legal.form;

  const setField = (field: keyof LegalFormData, value: string) =>
    legal.form.setField(field, value);

  return (
    <div
      className="scd-drawer-form"
      data-testid="contractor-detail-legal-step-self-employed"
    >
      <FormField
        label="ФИО"
        error={errors.selfFullName}
        required
        tooltip="Полные фамилия, имя и отчество"
      >
        <TextField fullWidth>
          <Input
            placeholder="Иванов Иван Иванович"
            value={form.selfFullName}
            onChange={(e) => setField("selfFullName", e.target.value)}
          />
        </TextField>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="ИНН"
          error={errors.selfInn}
          required
          tooltip="ИНН физического лица. 12 цифр"
        >
          <TextField fullWidth>
            <Input
              placeholder="12 цифр"
              value={form.selfInn}
              onChange={(e) =>
                setField("selfInn", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={12}
            />
          </TextField>
        </FormField>
        <FormField
          label="Телефон"
          error={errors.selfPhone}
          tooltip="Контактный телефон. Формат: +7XXXXXXXXXX"
        >
          <TextField fullWidth>
            <Input
              placeholder="+79001234567"
              value={applyPhoneMask(form.selfPhone)}
              onChange={(e) => {
                const raw = phoneMaskToRaw(e.target.value);
                setField("selfPhone", raw);
              }}
              maxLength={12}
            />
          </TextField>
        </FormField>
      </div>
      <div className="scd-drawer-row">
        <FormField
          label="Серия паспорта"
          error={errors.selfPassportSeries}
          tooltip="Серия паспорта, 4 цифры"
        >
          <TextField fullWidth>
            <Input
              placeholder="4 цифры"
              value={form.selfPassportSeries}
              onChange={(e) =>
                setField(
                  "selfPassportSeries",
                  e.target.value.replace(/\D/g, ""),
                )
              }
              onKeyDown={numericOnly}
              maxLength={4}
            />
          </TextField>
        </FormField>
        <FormField
          label="Номер паспорта"
          error={errors.selfPassportNumber}
          tooltip="Номер паспорта, 6 цифр"
        >
          <TextField fullWidth>
            <Input
              placeholder="6 цифр"
              value={form.selfPassportNumber}
              onChange={(e) =>
                setField(
                  "selfPassportNumber",
                  e.target.value.replace(/\D/g, ""),
                )
              }
              onKeyDown={numericOnly}
              maxLength={6}
            />
          </TextField>
        </FormField>
      </div>
      <FormField
        label="Кем выдан"
        error={errors.selfPassportIssuedBy}
        tooltip="Орган, выдавший паспорт"
      >
        <TextField fullWidth>
          <Input
            placeholder="УФМС России по г. Москве"
            value={form.selfPassportIssuedBy}
            onChange={(e) => setField("selfPassportIssuedBy", e.target.value)}
          />
        </TextField>
      </FormField>
      <FormField
        label="Дата выдачи"
        error={errors.selfPassportDate}
        tooltip="Дата выдачи паспорта"
      >
        {(() => {
          let dateValue: CalendarDate | null = null;
          try {
            if (form.selfPassportDate)
              dateValue = parseDate(form.selfPassportDate);
          } catch {
            /* ignore invalid */
          }
          return (
            <DateField
              fullWidth
              value={dateValue}
              onChange={(d) =>
                setField("selfPassportDate", d ? d.toString() : "")
              }
            >
              <DateField.Group fullWidth>
                <DateField.InputContainer>
                  <DateField.Input>
                    {(segment) => <DateField.Segment segment={segment} />}
                  </DateField.Input>
                </DateField.InputContainer>
              </DateField.Group>
            </DateField>
          );
        })()}
      </FormField>
    </div>
  );
});

StepSelfEmployed.displayName = "StepSelfEmployed";
export default StepSelfEmployed;
